import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { getMovieDetails, getTvDetails, getSeasonDetails, getMovieSequel, getSimilar, searchMovies } from '../services/tmdb';
import { parsePlayerEvent, createProgressHandler, getSavedProgress } from '../services/player';
import {
  getMovieStreamUrls,
  getTvStreamUrls,
  getSandboxCompatibleSources,
  getBestSource,
  getNextSource,
  updateStreamHealth,
  type StreamSource,
} from '../services/streaming';
import {
  getAutoPlaySettings,
  askGeminiForNextMovie,
  addToWatchHistory,
  getWatchHistory,
} from '../services/autoplay';
import UpNextOverlay from '../components/player/UpNextOverlay';
import SafePlayerFrame from '../components/player/SafePlayerFrame';
import { useStore } from '../store/useStore';
import type { Movie, TvShow, Episode } from '../types';

function preferredSubtitleLanguage(): string {
  const language = navigator.language.split('-')[0]?.toLowerCase();
  return /^[a-z]{2,3}$/.test(language) ? language : 'en';
}

export default function Watch() {
  const { type, id } = useParams<{ type: 'movie' | 'tv'; id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [media, setMedia] = useState<Movie | TvShow | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [showEpisodes, setShowEpisodes] = useState(false);
  const [showSources, setShowSources] = useState(false);

  // Multi-source state
  const [sources, setSources] = useState<StreamSource[]>([]);
  const [activeSource, setActiveSource] = useState<StreamSource | null>(null);
  const [streamStatus, setStreamStatus] = useState<'loading' | 'playing' | 'error' | 'switching'>('loading');
  const [autoSwitch, setAutoSwitch] = useState(true);
  const [loadStartTime, setLoadStartTime] = useState<number>(0);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamStatusRef = useRef(streamStatus);
  const handleStreamErrorRef = useRef<() => void>(() => {});
  const savedProgressRef = useRef<number>(0);

  // Auto-play states
  const [showUpNext, setShowUpNext] = useState(false);
  const [upNextMedia, setUpNextMedia] = useState<Movie | TvShow | null>(null);
  const [upNextReason, setUpNextReason] = useState<string>('');

  const { themeColor } = useStore();
  const autoPlaySettings = getAutoPlaySettings();

  // Get season and episode from URL params (for TV)
  const season = parseInt(searchParams.get('s') || '1');
  const episode = parseInt(searchParams.get('e') || '1');

  // Current episode info (for TV)
  const currentEpisode = episodes.find(e => e.episode_number === episode);

  // Find next content to auto-play (for movies)
  const findNextMovie = useCallback(async (currentMovie: Movie) => {
    if (!autoPlaySettings.enabled) return;

    // 1. Check for sequel in collection
    const sequel = await getMovieSequel(currentMovie.id);
    if (sequel) {
      setUpNextMedia(sequel);
      setUpNextReason(`Part ${sequel.title.match(/\d+/)?.[0] || 'next'} of the series`);
      setShowUpNext(true);
      return;
    }

    // 2. Ask Gemini for smart recommendation
    const watchHistory = getWatchHistory();
    const geminiRec = await askGeminiForNextMovie(currentMovie, watchHistory);
    if (geminiRec) {
      // Search TMDB for the recommended movie
      const searchResults = await searchMovies(geminiRec.title);
      const matchedMovie = searchResults.results.find(
        m => m.title.toLowerCase().includes(geminiRec.title.toLowerCase()) ||
             geminiRec.title.toLowerCase().includes(m.title.toLowerCase())
      );
      if (matchedMovie) {
        const fullMovie = await getMovieDetails(matchedMovie.id);
        setUpNextMedia(fullMovie);
        setUpNextReason(geminiRec.reason);
        setShowUpNext(true);
        return;
      }
    }

    // 3. Fallback to TMDB similar movies
    const similar = await getSimilar('movie', currentMovie.id);
    if (similar.results.length > 0) {
      // Pick first result that's not in watch history
      const nextMovie = similar.results.find(
        m => !watchHistory.includes('title' in m ? m.title : (m as TvShow).name)
      ) || similar.results[0];

      const fullMovie = await getMovieDetails(nextMovie.id);
      setUpNextMedia(fullMovie as Movie);
      setUpNextReason('Similar to what you just watched');
      setShowUpNext(true);
    }
  }, [autoPlaySettings.enabled]);

  // Get theme color hex
  const getThemeColorHex = () => {
    const themeMap: Record<string, string> = {
      red: 'e50914',
      blue: '0dcaf0',
      purple: '9146ff',
      green: '1db954',
      orange: 'ff6b35',
      pink: 'ec4899',
    };
    return themeMap[themeColor] || 'e50914';
  };

  // Build provider-specific URLs without corrupting path-based TMDB IDs.
  // VidFast documents a default subtitle-language parameter; its own CC/audio
  // menus remain responsible for changing tracks during playback.
  const buildIframeSrc = (source: StreamSource): string => {
    if (source.id === 'vidfast') {
      const url = new URL(source.url);
      url.searchParams.set('autoPlay', 'true');
      url.searchParams.set('sub', preferredSubtitleLanguage());
      if (savedProgressRef.current > 0) {
        url.searchParams.set('startAt', String(Math.floor(savedProgressRef.current)));
      }
      return url.toString();
    }

    if (source.id !== 'vidking') return source.url;
    const params = new URLSearchParams();
    params.set('color', getThemeColorHex());
    params.set('autoPlay', 'true');
    if (savedProgressRef.current > 0) {
      params.set('progress', String(Math.floor(savedProgressRef.current)));
    }
    return `${source.url}?${params.toString()}`;
  };

  // Fetch media data and setup sources
  useEffect(() => {
    const fetchMedia = async () => {
      if (!id || !type) return;
      try {
        setLoading(true);
        setStreamStatus('loading');

        if (type === 'movie') {
          const data = await getMovieDetails(parseInt(id));
          setMedia(data);

          // Get movie sources
          const movieSources = getSandboxCompatibleSources(
            getMovieStreamUrls(data.id, getThemeColorHex())
          );
          setSources(movieSources);
          setActiveSource(getBestSource(movieSources));
        } else {
          const [showData, seasonData] = await Promise.all([
            getTvDetails(parseInt(id)),
            getSeasonDetails(parseInt(id), season),
          ]);
          setMedia(showData);
          setEpisodes(seasonData.episodes || []);

          // Get TV sources
          const tvSources = getSandboxCompatibleSources(
            getTvStreamUrls(showData.id, season, episode, getThemeColorHex())
          );
          setSources(tvSources);
          setActiveSource(getBestSource(tvSources));
        }
      } catch (error) {
        console.error('Error fetching media:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, [id, type, season, episode, themeColor]);

  // Get saved progress for resume - only compute once per media/episode load
  // Using a ref prevents iframe URL from changing on re-renders (which would reload the iframe)
  useEffect(() => {
    if (media) {
      savedProgressRef.current = getSavedProgress(type!, media.id, type === 'tv' ? season : undefined, type === 'tv' ? episode : undefined);
    } else {
      savedProgressRef.current = 0;
    }
  }, [media?.id, type, season, episode]);

  // Keep refs in sync for async callbacks
  useEffect(() => {
    streamStatusRef.current = streamStatus;
  }, [streamStatus]);

  // Switch to a different source
  const switchSource = useCallback((source: StreamSource) => {
    setStreamStatus('switching');
    setLoadStartTime(Date.now());
    setActiveSource(source);

    // Clear any pending error timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }

    // Set a timeout to detect if stream doesn't load
    // Use refs to avoid stale closure
    errorTimeoutRef.current = setTimeout(() => {
      if (streamStatusRef.current === 'switching' || streamStatusRef.current === 'loading') {
        handleStreamErrorRef.current();
      }
    }, 8000); // 8 second timeout — switch faster when sources are dead
  }, []);

  // Handle stream errors - auto switch to next source
  const handleStreamError = useCallback(() => {
    if (!activeSource || !autoSwitch) {
      setStreamStatus('error');
      return;
    }

    const nextSource = getNextSource(sources, activeSource.id);
    if (nextSource) {
      console.log(`Stream error on ${activeSource.name}, switching to ${nextSource.name}`);
      switchSource(nextSource);
    } else {
      setStreamStatus('error');
    }
  }, [activeSource, sources, autoSwitch, switchSource]);

  // Keep handleStreamErrorRef in sync
  useEffect(() => {
    handleStreamErrorRef.current = handleStreamError;
  }, [handleStreamError]);

  // Handle iframe load
  const handleIframeLoad = useCallback(() => {
    const loadTime = Date.now() - loadStartTime;
    if (activeSource) {
      updateStreamHealth(activeSource.id, true, loadTime);
    }
    setStreamStatus('playing');

    // Clear error timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
  }, [activeSource, loadStartTime]);

  // Handle player messages
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!media) return;

      const playerEvent = parsePlayerEvent(event.data);
      if (!playerEvent) return;

      // Stream is working if we're getting messages
      if (streamStatus !== 'playing') {
        setStreamStatus('playing');
        if (activeSource) {
          updateStreamHealth(activeSource.id, true);
        }
      }

      const title = 'title' in media ? media.title : media.name;
      const handler = createProgressHandler(
        type!,
        media.id,
        type === 'tv' ? `${title} S${season}E${episode}` : title,
        media.poster_path,
        type === 'tv' ? season : undefined,
        type === 'tv' ? episode : undefined
      );

      handler(playerEvent);

      // Handle video ended event
      if (playerEvent.data.event === 'ended') {
        // Add to watch history
        if (media) {
          const title = 'title' in media ? media.title : media.name;
          addToWatchHistory(title);
        }

        if (type === 'tv') {
          // Auto-advance to next episode for TV
          const nextEp = episodes.find(e => e.episode_number === episode + 1);
          if (nextEp) {
            // Show up next overlay with countdown
            setUpNextMedia(media);
            setUpNextReason(`S${season} E${episode + 1} - ${nextEp.name}`);
            setShowUpNext(true);
          }
        } else if (type === 'movie' && media) {
          // Find next movie recommendation
          findNextMovie(media as Movie);
        }
      }
    },
    [media, type, season, episode, episodes, id, navigate, streamStatus, activeSource, findNextMovie]
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Auto-hide controls
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowControls(false), 3000);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(timeout);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        navigate(-1);
      }
      // Number keys to switch sources
      if (e.key >= '1' && e.key <= '9') {
        const index = parseInt(e.key) - 1;
        if (sources[index]) {
          switchSource(sources[index]);
        }
      }
      // 's' to toggle source panel
      if (e.key === 's' || e.key === 'S') {
        setShowSources(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, sources, switchSource]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  const mediaTitle = media ? ('title' in media ? media.title : media.name) : 'Loading...';

  return (
    <div className="fixed inset-0 bg-black z-50">
      {/* Top Bar */}
      <div
        className={`absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="font-semibold text-lg">{mediaTitle}</h1>
              {type === 'tv' && currentEpisode && (
                <p className="text-sm text-gray-400">
                  S{season} E{episode} - {currentEpisode.name}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Stream Status */}
            <div className="flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full">
              <span className={`w-2 h-2 rounded-full ${
                streamStatus === 'playing' ? 'bg-green-500' :
                streamStatus === 'loading' || streamStatus === 'switching' ? 'bg-yellow-500 animate-pulse' :
                'bg-red-500'
              }`} />
              <span className="text-sm">
                {streamStatus === 'playing' ? activeSource?.name :
                 streamStatus === 'switching' ? 'Switching...' :
                 streamStatus === 'loading' ? 'Loading...' :
                 'Error'}
              </span>
            </div>

            {/* Auto-switch toggle */}
            <button
              onClick={() => setAutoSwitch(!autoSwitch)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                autoSwitch ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-gray-400'
              }`}
              title="Auto-switch to next source if current fails"
            >
              Auto: {autoSwitch ? 'ON' : 'OFF'}
            </button>

            {/* Source selector button */}
            <button
              onClick={() => setShowSources(!showSources)}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
              </svg>
              Sources ({sources.length})
            </button>

            {/* Episode selector button (TV only) */}
            {type === 'tv' && (
              <button
                onClick={() => setShowEpisodes(!showEpisodes)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                Episodes
              </button>
            )}

            {/* Back to details */}
            <Link
              to={`/${type}/${id}`}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
              title="More Info"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Source Selector Sidebar */}
      {showSources && (
        <div className="liquid-glass absolute top-0 right-0 bottom-0 w-80 z-20 overflow-y-auto animate-slide-in-right">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl">Stream Sources</h2>
              <button
                onClick={() => setShowSources(false)}
                className="p-1 hover:bg-white/10 rounded"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-sm text-gray-400 mb-4">
              Press 1-{sources.length} to quick switch. If a source buffers, try another!
            </p>

            <div className="space-y-2">
              {sources.map((source, index) => (
                <button
                  key={source.id}
                  onClick={() => {
                    switchSource(source);
                    setShowSources(false);
                  }}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    activeSource?.id === source.id
                      ? 'bg-[var(--color-primary)]'
                      : 'hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <kbd className="liquid-glass w-7 h-7 flex items-center justify-center rounded-md ring-1 ring-white/15 text-sm font-mono font-bold text-white shadow-sm">
                        {index + 1}
                      </kbd>
                      <div>
                        <p className="font-medium">{source.name}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <span className={`px-1.5 py-0.5 rounded ${
                            source.quality === '4K' ? 'bg-purple-500/20 text-purple-400' :
                            source.quality === 'HD' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {source.quality}
                          </span>
                          {source.hasAds && <span className="text-yellow-500">Has Ads</span>}
                          {source.autoNext && <span className="text-green-500">Auto-Next</span>}
                        </div>
                      </div>
                    </div>
                    {activeSource?.id === source.id && (
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-4 p-3 bg-white/5 rounded-lg">
              <p className="text-xs text-gray-400">
                <strong>Tips:</strong><br />
                • If video buffers, switch sources<br />
                • CC and audio tracks are inside the video player; availability varies by source<br />
                • 4K sources have best quality<br />
                • Auto-switch will find working links automatically
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Episode Sidebar */}
      {showEpisodes && type === 'tv' && (
        <div className="absolute top-0 right-0 bottom-0 w-80 bg-black/95 z-20 overflow-y-auto animate-slide-in-right">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Season {season}</h2>
              <button
                onClick={() => setShowEpisodes(false)}
                className="p-1 hover:bg-white/10 rounded"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-2">
              {episodes.map((ep) => (
                <button
                  key={ep.id}
                  onClick={() => {
                    navigate(`/watch/tv/${id}?s=${season}&e=${ep.episode_number}`, { replace: true });
                    setShowEpisodes(false);
                  }}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    ep.episode_number === episode
                      ? 'bg-[var(--color-primary)]'
                      : 'hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold w-8">{ep.episode_number}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{ep.name}</p>
                      {ep.runtime && (
                        <p className="text-xs text-gray-400">{ep.runtime} min</p>
                      )}
                    </div>
                    {ep.episode_number === episode && (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Player */}
      {loading || !activeSource ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-white/20 border-t-[var(--color-primary)] rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading stream sources...</p>
          </div>
        </div>
      ) : streamStatus === 'error' ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">😢</div>
            <h2 className="text-xl font-semibold mb-2">All sources failed</h2>
            <p className="text-gray-400 mb-4">
              Try refreshing or check back later. Sometimes streams take a moment to load.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-[var(--color-primary)] rounded-lg font-semibold"
              >
                Refresh
              </button>
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 bg-white/10 rounded-lg"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      ) : (
        <SafePlayerFrame
          ref={iframeRef}
          src={buildIframeSrc(activeSource)}
          title={`${media ? ('title' in media ? media.title : media.name) : 'CineVault'} — ${activeSource.name} player`}
          sandboxPolicy="trusted-no-ads"
          onLoad={handleIframeLoad}
          onError={handleStreamError}
        />
      )}

      {/* Keyboard shortcut hint bar — always discoverable, fades with controls */}
      {!loading && activeSource && streamStatus !== 'error' && sources.length > 0 && (
        <div
          className={`absolute bottom-4 left-1/2 -translate-x-1/2 z-10 liquid-glass rounded-full px-4 py-1.5 text-xs text-white/70 transition-opacity duration-300 ease-bounce ${
            showControls ? 'opacity-100' : 'opacity-30'
          }`}
        >
          Press <kbd className="font-mono">[1]</kbd>-<kbd className="font-mono">[{sources.length}]</kbd> to switch source · <kbd className="font-mono">[S]</kbd> panel
        </div>
      )}

      {/* Next Episode Hint (TV only) — positioned above player controls to avoid fullscreen button */}
      {type === 'tv' && episodes.find(e => e.episode_number === episode + 1) && (
        <div
          className={`absolute bottom-16 right-4 z-10 transition-opacity duration-300 ${
            showControls ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <button
            onClick={() => navigate(`/watch/tv/${id}?s=${season}&e=${episode + 1}`, { replace: true })}
            className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg font-semibold hover:bg-gray-200 transition-colors shadow-lg"
          >
            Next Episode
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Stream switching indicator */}
      {streamStatus === 'switching' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-30">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-white/20 border-t-[var(--color-primary)] rounded-full animate-spin mx-auto mb-3" />
            <p className="text-white">Switching to {activeSource?.name}...</p>
          </div>
        </div>
      )}

      {/* Up Next Overlay (auto-play) */}
      {showUpNext && upNextMedia && (
        <UpNextOverlay
          nextMedia={upNextMedia}
          type={type!}
          reason={upNextReason}
          nextSeason={type === 'tv' ? season : undefined}
          nextEpisode={type === 'tv' ? episode + 1 : undefined}
          episodeTitle={type === 'tv' ? episodes.find(e => e.episode_number === episode + 1)?.name : undefined}
          onPlay={() => {
            setShowUpNext(false);
            if (type === 'tv') {
              navigate(`/watch/tv/${id}?s=${season}&e=${episode + 1}`, { replace: true });
            } else if (upNextMedia) {
              navigate(`/watch/movie/${upNextMedia.id}`, { replace: true });
            }
          }}
          onCancel={() => {
            setShowUpNext(false);
          }}
          autoStart={autoPlaySettings.enabled}
        />
      )}
    </div>
  );
}
